import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  AccountBalance as BalanceIcon,
  Lock as FreezeIcon,
  LockOpen as UnfreezeIcon,
  Gavel as AppealIcon,
  AttachMoney as IncomeIcon,
} from "@mui/icons-material";
import api from "../services/api";

interface Trader {
  id: string;
  name: string;
  balance: number;
  frozenBalance: number;
  transitBalance: number;
  disputeBalance: number;
  status: string;
}

interface BalanceSnapshot {
  balance: number;
  frozenBalance: number;
  transitBalance: number;
  disputeBalance: number;
  timestamp: string;
}

interface ScenarioResult {
  success: boolean;
  scenario: string;
  before: BalanceSnapshot;
  after: BalanceSnapshot;
  changes: {
    balance: number;
    frozenBalance: number;
    transitBalance: number;
    disputeBalance: number;
  };
  message: string;
  details?: any;
}

interface LogEntry {
  timestamp: string;
  scenario: string;
  success: boolean;
  message: string;
  changes?: any;
}

interface Merchant {
  id: string;
  name: string;
  balance: number;
  status: string;
}

interface BalanceHistoryLog {
  id: string;
  type: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  balanceType: string;
  description: string;
  metadata: any;
  createdAt: string;
}

const BalanceTests: React.FC = () => {
  const [traders, setTraders] = useState<Trader[]>([]);
  const [selectedTraderId, setSelectedTraderId] = useState<string>("");
  const [selectedTrader, setSelectedTrader] = useState<Trader | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  
  // Scenario parameters
  const [freezeAmount, setFreezeAmount] = useState<number>(200);
  const [unfreezeAmount, setUnfreezeAmount] = useState<number>(200);
  const [incomeAmount, setIncomeAmount] = useState<number>(7);
  const [appealAmount, setAppealAmount] = useState<number>(200);
  const [fullOrderAmount, setFullOrderAmount] = useState<number>(200);
  const [merchantFee, setMerchantFee] = useState<number>(12);
  const [traderFee, setTraderFee] = useState<number>(10);
  const [appealResolution, setAppealResolution] = useState<"APPROVED" | "REJECTED">("APPROVED");
  
  // New: Merchants and Balance History
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>("");
  const [balanceHistoryLogs, setBalanceHistoryLogs] = useState<BalanceHistoryLog[]>([]);
  const [showHistoryLogs, setShowHistoryLogs] = useState(false);

  // Load traders
  useEffect(() => {
    loadTraders();
    loadMerchants();
  }, []);

  const loadTraders = async () => {
    try {
      setLoading(true);
      const response = await api.getTestTraders();
      setTraders(response.data || []);
    } catch (error: any) {
      console.error("Error loading traders:", error);
      addLog("load-traders", false, `Failed to load traders: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMerchants = async () => {
    try {
      const response = await api.getTestMerchants();
      setMerchants(response.data || []);
    } catch (error: any) {
      console.error("Error loading merchants:", error);
    }
  };

  const loadBalanceHistory = async () => {
    if (!selectedTraderId) return;
    try {
      const response = await api.getBalanceLogs(selectedTraderId);
      setBalanceHistoryLogs(response.logs || []);
      setShowHistoryLogs(true);
    } catch (error: any) {
      console.error("Error loading balance history:", error);
      addLog("load-history", false, `Failed to load history: ${error.message}`);
    }
  };

  const loadTraderBalances = useCallback(async () => {
    if (!selectedTraderId) return;
    
    try {
      const response = await api.getTraderBalances(selectedTraderId);
      const trader = traders.find(t => t.id === selectedTraderId);
      if (trader) {
        setSelectedTrader({
          ...trader,
          balance: response.balances.balance,
          frozenBalance: response.balances.frozenBalance,
          transitBalance: response.balances.transitBalance,
          disputeBalance: response.balances.disputeBalance,
        });
      }
    } catch (error: any) {
      console.error("Error loading balances:", error);
    }
  }, [selectedTraderId, traders]);

  useEffect(() => {
    if (selectedTraderId) {
      loadTraderBalances();
    }
  }, [selectedTraderId, loadTraderBalances]);

  const addLog = (scenario: string, success: boolean, message: string, changes?: any) => {
    const entry: LogEntry = {
      timestamp: new Date().toLocaleTimeString(),
      scenario,
      success,
      message,
      changes,
    };
    setLogs(prev => [entry, ...prev.slice(0, 49)]); // Keep last 50 logs
  };

  const handleScenarioResult = (result: ScenarioResult) => {
    addLog(result.scenario, result.success, result.message, result.changes);
    loadTraderBalances();
  };

  // Scenario handlers
  const runFreeze = async () => {
    if (!selectedTraderId) return;
    setActionLoading("freeze");
    try {
      const result = await api.runScenarioFreeze(selectedTraderId, freezeAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("freeze", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runUnfreezeSuccess = async () => {
    if (!selectedTraderId) return;
    setActionLoading("unfreeze-success");
    try {
      const result = await api.runScenarioUnfreezeSuccess(selectedTraderId, unfreezeAmount, incomeAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("unfreeze-success", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runUnfreezeCancel = async () => {
    if (!selectedTraderId) return;
    setActionLoading("unfreeze-cancel");
    try {
      const result = await api.runScenarioUnfreezeCancel(selectedTraderId, unfreezeAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("unfreeze-cancel", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runCreditIncome = async () => {
    if (!selectedTraderId) return;
    setActionLoading("credit-income");
    try {
      const result = await api.runScenarioCreditIncome(selectedTraderId, incomeAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("credit-income", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runHoldAppeal = async () => {
    if (!selectedTraderId) return;
    setActionLoading("hold-appeal");
    try {
      const result = await api.runScenarioHoldAppeal(selectedTraderId, appealAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("hold-appeal", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runReleaseAppeal = async () => {
    if (!selectedTraderId) return;
    setActionLoading("release-appeal");
    try {
      const result = await api.runScenarioReleaseAppeal(selectedTraderId, appealAmount);
      handleScenarioResult(result);
    } catch (error: any) {
      addLog("release-appeal", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runFullOrderScenario = async () => {
    if (!selectedTraderId) return;
    setActionLoading("full-order");
    try {
      const result = await api.runFullOrderScenario(selectedTraderId, fullOrderAmount, merchantFee, traderFee);
      result.steps?.forEach((step: ScenarioResult) => {
        addLog(step.scenario, step.success, step.message, step.changes);
      });
      addLog("full-order", result.success, result.summary);
      loadTraderBalances();
    } catch (error: any) {
      addLog("full-order", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runAppealFlowScenario = async () => {
    if (!selectedTraderId) return;
    setActionLoading("appeal-flow");
    try {
      const result = await api.runAppealFlowScenario(selectedTraderId, appealAmount, appealResolution);
      result.steps?.forEach((step: ScenarioResult) => {
        addLog(step.scenario, step.success, step.message, step.changes);
      });
      addLog("appeal-flow", result.success, result.summary);
      loadTraderBalances();
    } catch (error: any) {
      addLog("appeal-flow", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const runFullSystemTest = async () => {
    if (!selectedTraderId || !selectedMerchantId) {
      addLog("full-system-test", false, "Please select both trader and merchant");
      return;
    }
    setActionLoading("full-system-test");
    try {
      const result = await api.runFullSystemTest(
        selectedTraderId,
        selectedMerchantId,
        fullOrderAmount,
        merchantFee,
        traderFee
      );
      
      // Log each step result
      result.results?.forEach((step: any) => {
        if (step.scenario) {
          addLog(step.step || step.scenario, step.success !== false, step.message || step.scenario, step.changes);
        }
      });
      
      // Log summary
      addLog(
        "full-system-test",
        result.success,
        `Test ${result.testId}: Trader ${result.initialTraderBalance?.toFixed(2)} → ${result.finalTraderBalance?.toFixed(2)} (${result.traderBalanceChange >= 0 ? "+" : ""}${result.traderBalanceChange?.toFixed(2)})`
      );
      
      loadTraderBalances();
    } catch (error: any) {
      addLog("full-system-test", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const resetBalances = async () => {
    if (!selectedTraderId) return;
    setActionLoading("reset");
    try {
      const result = await api.resetBalances(selectedTraderId, {
        balance: 10000,
        frozenBalance: 0,
        transitBalance: 0,
        disputeBalance: 0,
      });
      addLog("reset", result.success, "Balances reset to default values");
      loadTraderBalances();
    } catch (error: any) {
      addLog("reset", false, `Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatBalance = (value: number) => {
    return value.toFixed(2);
  };

  const formatChange = (value: number) => {
    if (value === 0) return "—";
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}`;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BalanceIcon /> Balance Testing Scenarios
      </Typography>

      {/* Trader & Merchant Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Trader</InputLabel>
              <Select
                value={selectedTraderId}
                label="Select Trader"
                onChange={(e) => setSelectedTraderId(e.target.value)}
                disabled={loading}
              >
                {traders.map((trader) => (
                  <MenuItem key={trader.id} value={trader.id}>
                    {trader.name} (Balance: {formatBalance(trader.balance)} USDT)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Select Merchant</InputLabel>
              <Select
                value={selectedMerchantId}
                label="Select Merchant"
                onChange={(e) => setSelectedMerchantId(e.target.value)}
              >
                {merchants.map((merchant) => (
                  <MenuItem key={merchant.id} value={merchant.id}>
                    {merchant.name} (Balance: {merchant.balance?.toFixed(2) || 0} USDT)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadTraders}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
              {selectedTraderId && (
                <>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadTraderBalances}
                    size="small"
                  >
                    Balances
                  </Button>
                  <Button
                    onClick={loadBalanceHistory}
                    size="small"
                    color="info"
                  >
                    View Logs
                  </Button>
                  <Button
                    onClick={resetBalances}
                    size="small"
                    color="warning"
                    disabled={actionLoading !== null}
                  >
                    Reset
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Current Balances */}
      {selectedTrader && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Balances: {selectedTrader.name}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: "success.light", color: "success.contrastText" }}>
                <CardContent>
                  <Typography variant="subtitle2">Working Balance</Typography>
                  <Typography variant="h5">{formatBalance(selectedTrader.balance)} USDT</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: "info.light", color: "info.contrastText" }}>
                <CardContent>
                  <Typography variant="subtitle2">Frozen Balance</Typography>
                  <Typography variant="h5">{formatBalance(selectedTrader.frozenBalance)} USDT</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: selectedTrader.transitBalance < 0 ? "warning.light" : "secondary.light" }}>
                <CardContent>
                  <Typography variant="subtitle2">Transit Balance</Typography>
                  <Typography variant="h5">{formatBalance(selectedTrader.transitBalance)} USDT</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ bgcolor: selectedTrader.disputeBalance > 0 ? "error.light" : "grey.300" }}>
                <CardContent>
                  <Typography variant="subtitle2">Dispute Balance</Typography>
                  <Typography variant="h5">{formatBalance(selectedTrader.disputeBalance)} USDT</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Test Scenarios */}
      {selectedTraderId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test Scenarios
          </Typography>
          
          <Grid container spacing={3}>
            {/* Scenario 1: Freeze */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FreezeIcon color="primary" /> 1. Freeze Balance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Simulates order creation: Working → Frozen
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        label="Amount (USDT)"
                        type="number"
                        size="small"
                        fullWidth
                        value={freezeAmount}
                        onChange={(e) => setFreezeAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        startIcon={actionLoading === "freeze" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runFreeze}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scenario 2: Unfreeze Success */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <UnfreezeIcon color="success" /> 2. Order Success
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Simulates order completion: Unfreeze + Credit Income
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={unfreezeAmount}
                        onChange={(e) => setUnfreezeAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Income"
                        type="number"
                        size="small"
                        fullWidth
                        value={incomeAmount}
                        onChange={(e) => setIncomeAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={actionLoading === "unfreeze-success" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runUnfreezeSuccess}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scenario 3: Unfreeze Cancel */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <UnfreezeIcon color="warning" /> 3. Order Cancel
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Simulates order cancellation: Frozen → Working (return)
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        label="Amount (USDT)"
                        type="number"
                        size="small"
                        fullWidth
                        value={unfreezeAmount}
                        onChange={(e) => setUnfreezeAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={actionLoading === "unfreeze-cancel" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runUnfreezeCancel}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scenario 4: Credit Income */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IncomeIcon color="success" /> 4. Credit Income
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Credit earnings: → Transit (if deficit) or → Working
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        label="Amount (USDT)"
                        type="number"
                        size="small"
                        fullWidth
                        value={incomeAmount}
                        onChange={(e) => setIncomeAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={actionLoading === "credit-income" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runCreditIncome}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scenario 5: Hold Appeal */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AppealIcon color="error" /> 5. Hold for Appeal
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Simulates appeal creation: Working → Dispute
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        label="Amount (USDT)"
                        type="number"
                        size="small"
                        fullWidth
                        value={appealAmount}
                        onChange={(e) => setAppealAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={actionLoading === "hold-appeal" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runHoldAppeal}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Scenario 6: Release Appeal */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AppealIcon color="success" /> 6. Release Appeal
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Simulates appeal approval: Dispute → Working
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={6}>
                      <TextField
                        label="Amount (USDT)"
                        type="number"
                        size="small"
                        fullWidth
                        value={appealAmount}
                        onChange={(e) => setAppealAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={actionLoading === "release-appeal" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runReleaseAppeal}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Complex Scenarios */}
          <Typography variant="subtitle1" gutterBottom>
            Complete Scenarios
          </Typography>
          
          <Grid container spacing={3}>
            {/* Full Order Flow */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: "primary.50" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlayIcon color="primary" /> Full Order Flow
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Complete order: Freeze → Success → Income
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={fullOrderAmount}
                        onChange={(e) => setFullOrderAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Merch %"
                        type="number"
                        size="small"
                        fullWidth
                        value={merchantFee}
                        onChange={(e) => setMerchantFee(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        label="Trader %"
                        type="number"
                        size="small"
                        fullWidth
                        value={traderFee}
                        onChange={(e) => setTraderFee(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <Button
                        variant="contained"
                        startIcon={actionLoading === "full-order" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runFullOrderScenario}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Appeal Flow */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ bgcolor: "warning.50" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AppealIcon color="warning" /> Appeal Flow
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Complete appeal: Freeze → Complete → Appeal → Resolve
                  </Typography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={4}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={appealAmount}
                        onChange={(e) => setAppealAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Resolution</InputLabel>
                        <Select
                          value={appealResolution}
                          label="Resolution"
                          onChange={(e) => setAppealResolution(e.target.value as "APPROVED" | "REJECTED")}
                        >
                          <MenuItem value="APPROVED">Approved</MenuItem>
                          <MenuItem value="REJECTED">Rejected</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={4}>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={actionLoading === "appeal-flow" ? <CircularProgress size={16} /> : <PlayIcon />}
                        onClick={runAppealFlowScenario}
                        disabled={actionLoading !== null}
                        fullWidth
                      >
                        Run
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Full System Test */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: "error.50", border: "2px solid", borderColor: "error.main" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlayIcon color="error" /> 🧪 FULL SYSTEM TEST
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Tests complete flow: Order Creation → Completion → Appeal on COMPLETED (no change) → New Order → Cancel → Appeal → Approve
                  </Typography>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    ⚙️ Uses: Trader {selectedTraderId?.slice(0, 8)}..., Merchant {selectedMerchantId?.slice(0, 8)}..., {fullOrderAmount} USDT @ {merchantFee}% merchant / {traderFee}% trader
                  </Alert>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2}>
                      <TextField
                        label="Amount"
                        type="number"
                        size="small"
                        fullWidth
                        value={fullOrderAmount}
                        onChange={(e) => setFullOrderAmount(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        label="Merch %"
                        type="number"
                        size="small"
                        fullWidth
                        value={merchantFee}
                        onChange={(e) => setMerchantFee(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <TextField
                        label="Trader %"
                        type="number"
                        size="small"
                        fullWidth
                        value={traderFee}
                        onChange={(e) => setTraderFee(Number(e.target.value))}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        variant="contained"
                        color="error"
                        size="large"
                        startIcon={actionLoading === "full-system-test" ? <CircularProgress size={20} /> : <PlayIcon />}
                        onClick={runFullSystemTest}
                        disabled={actionLoading !== null || !selectedTraderId || !selectedMerchantId}
                        fullWidth
                      >
                        Run Full System Test
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Operations Log */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Operations Log
          <Button size="small" onClick={() => setLogs([])}>Clear</Button>
        </Typography>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width={100}>Time</TableCell>
                <TableCell width={120}>Scenario</TableCell>
                <TableCell width={80}>Status</TableCell>
                <TableCell>Message</TableCell>
                <TableCell width={200}>Changes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">No operations yet. Run a scenario to see results.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log, index) => (
                  <TableRow key={index} sx={{ bgcolor: log.success ? "success.50" : "error.50" }}>
                    <TableCell>{log.timestamp}</TableCell>
                    <TableCell>
                      <Chip label={log.scenario} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {log.success ? (
                        <SuccessIcon color="success" fontSize="small" />
                      ) : (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell>{log.message}</TableCell>
                    <TableCell>
                      {log.changes && (
                        <Typography variant="caption" component="div">
                          W: {formatChange(log.changes.balance)} | 
                          F: {formatChange(log.changes.frozenBalance)} | 
                          T: {formatChange(log.changes.transitBalance)} | 
                          D: {formatChange(log.changes.disputeBalance)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default BalanceTests;
